import { useEffect, useState } from "react";
import { Upload, Trash2, ImagePlus, AlertCircle } from "lucide-react";
import BeforeAfter from "./BeforeAfter";
import { api, formatApiError } from "../lib/api";
import { compressImage } from "../lib/image";

const inputClass =
    "w-full bg-brand-elevated border border-brand-line rounded px-4 py-3 text-brand-cream text-base placeholder:text-brand-moss focus:outline-none focus:border-brand-amber transition-colors";

const PhotoPick = ({ label, file, onChange, testid }) => {
    const [preview, setPreview] = useState(null);
    useEffect(() => {
        if (!file) return setPreview(null);
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);
    return (
        <label
            className="relative flex flex-col items-center justify-center aspect-[4/3] rounded-lg border border-dashed border-brand-line bg-brand-elevated hover:border-brand-amber transition-colors cursor-pointer overflow-hidden"
            data-testid={`${testid}-label`}
        >
            {preview ? (
                <img src={preview} alt={`${label} preview`} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
                <>
                    <ImagePlus className="w-7 h-7 text-brand-moss mb-2" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-sage">{label} photo</span>
                    <span className="text-brand-moss text-xs mt-1">Tap to choose</span>
                </>
            )}
            <span className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-brand-ink/80 text-brand-cream px-2 py-0.5 rounded-full">
                {label}
            </span>
            <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                data-testid={testid}
                onChange={(e) => onChange(e.target.files?.[0] || null)}
            />
        </label>
    );
};

const AdminGallery = () => {
    const [jobs, setJobs] = useState(null);
    const [form, setForm] = useState({ title: "", location: "", size: "", note: "" });
    const [before, setBefore] = useState(null);
    const [after, setAfter] = useState(null);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");
    const [busyId, setBusyId] = useState(null);

    const load = () => api.get("/gallery").then(({ data }) => setJobs(data)).catch(() => setJobs([]));
    useEffect(() => {
        load();
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!before || !after) return setError("Pick both a before and an after photo.");
        setStatus("loading");
        setError("");
        try {
            const [b, a] = await Promise.all([compressImage(before), compressImage(after)]);
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            fd.append("before", b);
            fd.append("after", a);
            const { data } = await api.post("/admin/gallery", fd);
            setJobs((j) => [data, ...(j || [])]);
            setForm({ title: "", location: "", size: "", note: "" });
            setBefore(null);
            setAfter(null);
            setStatus("idle");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail, err.message || "Upload failed."));
            setStatus("idle");
        }
    };

    const remove = async (id) => {
        setBusyId(id);
        try {
            await api.delete(`/admin/gallery/${id}`);
            setJobs((j) => j.filter((x) => x.id !== id));
        } catch {
            setError("Couldn't remove that job.");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="admin-gallery" className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <form onSubmit={submit} className="lg:col-span-2 space-y-4" noValidate data-testid="gallery-upload-form">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-amber">Add a job</p>
                <div className="grid grid-cols-2 gap-3">
                    <PhotoPick label="Before" file={before} onChange={setBefore} testid="gallery-before-input" />
                    <PhotoPick label="After" file={after} onChange={setAfter} testid="gallery-after-input" />
                </div>
                <input placeholder="Job title (e.g. Oak stump, front yard)" value={form.title} onChange={set("title")} className={inputClass} data-testid="gallery-title-input" required />
                <div className="grid grid-cols-2 gap-3">
                    <input placeholder="Town (e.g. Dexter, MO)" value={form.location} onChange={set("location")} className={inputClass} data-testid="gallery-location-input" />
                    <input placeholder='Size (e.g. 30")' value={form.size} onChange={set("size")} className={inputClass} data-testid="gallery-size-input" />
                </div>
                <textarea placeholder="Short note (optional)" rows={2} value={form.note} onChange={set("note")} className={inputClass} data-testid="gallery-note-input" />
                {error && (
                    <p className="flex items-start gap-2 text-sm text-[#ff9b7a] bg-[#ff9b7a]/10 border border-[#ff9b7a]/30 rounded px-4 py-3" data-testid="gallery-error">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        {error}
                    </p>
                )}
                <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="gallery-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-3 bg-brand-amber text-brand-ink font-bold text-base px-8 py-3.5 rounded-full hover:bg-brand-amberHover transition-colors disabled:opacity-60"
                >
                    <Upload className="w-5 h-5" />
                    {status === "loading" ? "Uploading..." : "Publish to website"}
                </button>
                <p className="text-brand-moss text-xs leading-relaxed">
                    Photos are resized automatically. Shoot both from the same spot for the best slider effect.
                </p>
            </form>

            <div className="lg:col-span-3 space-y-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-amber" data-testid="gallery-admin-count">
                    {jobs === null ? "Loading..." : `${jobs.length} job${jobs.length === 1 ? "" : "s"} live on the site`}
                </p>
                {jobs?.length === 0 && (
                    <p className="text-brand-sage text-sm" data-testid="gallery-admin-empty">
                        No jobs yet. Add your first before/after pair and the gallery section will appear on the home page.
                    </p>
                )}
                {jobs?.map((job) => (
                    <div key={job.id} className="rounded-lg border border-brand-line bg-brand-surface p-4" data-testid={`gallery-admin-job-${job.id}`}>
                        <BeforeAfter job={job} />
                        <div className="mt-3 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="font-display font-bold uppercase text-brand-cream text-lg tracking-tight truncate">{job.title}</p>
                                <p className="text-brand-moss text-xs font-mono uppercase tracking-wider">
                                    {[job.location, job.size].filter(Boolean).join(" · ")}
                                </p>
                            </div>
                            <button
                                onClick={() => remove(job.id)}
                                disabled={busyId === job.id}
                                data-testid="gallery-admin-delete-btn"
                                className="shrink-0 inline-flex items-center gap-2 border border-brand-line text-brand-sage text-sm px-4 py-2 rounded-full hover:border-[#ff9b7a] hover:text-[#ff9b7a] transition-colors disabled:opacity-50"
                            >
                                <Trash2 className="w-4 h-4" />
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminGallery;
